"use client";

import { useListKey } from "modules/common/hooks/useListKey";

import { ReactNode, useEffect } from "react";
import { useQueryState } from "nuqs";
import { useInfiniteCityMatchedCards } from "modules/city/hooks/useInfiniteCityMatchedCards";
import { useCityUnifiedFilters } from "modules/city/hooks/useCityUnifiedFilters";
import { useFacetSearchParamsContract } from "modules/common/hooks/useFacetSearchParamsContract";
import TradeSectionTitle from "modules/lgs/components/stores/table/TradeSectionTitle";
import CityMatchedItemsView from "./CityMatchedItemsView";
import { useAuth } from "@clerk/nextjs";
import { EmptyStateCard } from "modules/common/components/EmptyStateCard";
import { useCityStoreStatus } from "modules/lgs/hooks/useCityStoreStatus";
import { ArrowRight } from "lucide-react";
import { cn } from "utils/utils";
import { useSetTabMeta } from "modules/city/stores/useCityTabMetaStore";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import Link from "next/link";
import MatchedCardsIcon from "modules/common/components/Icons/MatchedCardsIcon";

interface CityMatchedSectionClientProps {
  latitude: string;
  longitude: string;
  viewAllLink?: string;
  /** Optional mobile filter bar to display instead of title on mobile */
  mobileFilterBar?: ReactNode;
  /** When true, removes the card wrapper styling on mobile */
  hideCard?: boolean;
  /** View index from SSR cookie — eliminates flash by rendering correct view immediately */
  initialViewIndex?: 0 | 1;
  /** When true (desktop tab panel), skips card wrapper and TradeSectionTitle, registers metadata to store */
  embedded?: boolean;
  /** Limits rendered overview items without reducing the fetched page size. */
  maxItems?: number;
  /** When true, the count is rendered by the page's mainbar, so the desktop
   *  section-title row collapses. The row still renders on mobile, where it
   *  hosts the mobile filter bar. */
  countInMainbar?: boolean;
}

export function CityMatchedSectionClient(props: CityMatchedSectionClientProps) {
  const { embedded } = props;
  const { cityCardParsers } = useFacetSearchParamsContract();
  const { isSignedIn, isLoaded } = useAuth();

  // When embedded in desktop tabs, only fetch data for the active tab
  const [activeTab] = useQueryState("tab", {
    ...cityCardParsers.tab,
    shallow: true,
  });
  const isActiveTab = !embedded || activeTab === "matched";

  // Skip rendering entirely when embedded in an inactive tab — before heavy hooks
  if (embedded && !isActiveTab) return null;

  // If user is not signed in (and auth is loaded), show sign-up CTA before mounting heavy content
  if (isLoaded && !isSignedIn) {
    return <CityMatchedSignedOutState {...props} />;
  }

  return <CityMatchedSectionContent {...props} isLoaded={isLoaded} />;
}

function CityMatchedSignedOutState({
  embedded,
  hideCard,
  mobileFilterBar,
  countInMainbar,
}: CityMatchedSectionClientProps) {
  // Report zero metadata so the badge doesn't show a perpetual skeleton for anon users
  const setTabMeta = useSetTabMeta();
  useEffect(() => {
    if (!embedded) return;
    setTabMeta("matched", {
      currentCount: 0,
      total: 0,
      isLoading: false,
      hasMore: false,
    });
  }, [embedded, setTabMeta]);

  if (embedded) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-1 items-center justify-center overflow-hidden p-2">
          <EmptyStateCard
            variant="signed-out"
            icon={<MatchedCardsIcon className="h-8 w-8" />}
            title="Sign up"
            description="Sign in to see matches and connect with players in this city."
            showWaitlistCTA={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        hideCard
          ? "md:rounded-box md:bg-base-200 md:p-0 md:shadow-lg"
          : "rounded-box bg-base-200 p-2 shadow-lg"
      )}
    >
      <TradeSectionTitle
        countInMainbar={countInMainbar}
        currentCount={0}
        total={0}
        type="match"
        leftContent={mobileFilterBar}
      />
      <div className="flex flex-1 items-center justify-center overflow-hidden p-2">
        <EmptyStateCard
          variant="signed-out"
          icon={<MatchedCardsIcon className="h-8 w-8" />}
          title="Sign up"
          description="Sign in to see matches and connect with players in this city."
          showWaitlistCTA={true}
        />
      </div>
    </div>
  );
}

function CityMatchedSectionContent({
  latitude,
  longitude,
  viewAllLink,
  mobileFilterBar,
  hideCard,
  initialViewIndex,
  embedded,
  maxItems,
  countInMainbar,
  isLoaded,
}: CityMatchedSectionClientProps & { isLoaded: boolean }) {
  // Smart CTA: check if user has stores in this area.
  // This component only renders for signed-in users (parent short-circuits for anon),
  // so always provide coords.
  const storeStatusCoords = [parseFloat(longitude), parseFloat(latitude)] as [
    number,
    number,
  ];
  const {
    hasStoresInArea,
    noStoresFound,
    browseStoresUrl,
    isLoading: isStoreStatusLoading,
  } = useCityStoreStatus(storeStatusCoords);

  // Use the SAME unified filters that control other sections
  const {
    matchedFilters: cityMatchedCardsReq,
    activeView: unifiedActiveView,
    hasActiveFilters,
  } = useCityUnifiedFilters(latitude, longitude, initialViewIndex);
  const listKey = useListKey(cityMatchedCardsReq);

  // Use the existing infinite scroll hook for matched cards
  // React Query will use prefetched data from HydrationBoundary as initial data
  const {
    data: dataResultsMatched,
    totalResults: totalResultsMatched,
    currentCount,
    isPending: isPendingMatched,
    hasNextPage: hasNextPageMatched,
    isFetchingNextPage: isFetchingNextPageMatched,
    fetchNextPage: fetchNextPageMatched,
  } = useInfiniteCityMatchedCards(cityMatchedCardsReq);

  // Helper function to check if data is empty
  const isDataEmpty = (data: unknown) => {
    if (!data || typeof data !== "object") return true;
    const dataObj = data as { pages?: Array<{ items?: unknown[] }> };
    if (!dataObj.pages || dataObj.pages.length === 0) return true;
    const firstPage = dataObj.pages[0];
    return !firstPage || !firstPage.items || firstPage.items.length === 0;
  };

  // Treat auth loading as pending state to show skeleton
  const isAuthLoading = !isLoaded;
  const hasData =
    !isDataEmpty(dataResultsMatched) || isPendingMatched || isAuthLoading;
  const total = totalResultsMatched || 0;
  const displayedCount = maxItems
    ? Math.min(currentCount, maxItems)
    : currentCount;
  const hasAdditionalMatches =
    (hasNextPageMatched ?? false) || (!!maxItems && total > maxItems);

  // Report metadata to Zustand store so CityDesktopTabs badge stays in sync
  const setTabMeta = useSetTabMeta();
  useEffect(() => {
    if (!embedded) return;
    setTabMeta("matched", {
      currentCount: displayedCount,
      total,
      isLoading: isPendingMatched || isAuthLoading,
      hasMore: hasAdditionalMatches,
    });
  }, [
    embedded,
    displayedCount,
    total,
    isPendingMatched,
    isAuthLoading,
    hasAdditionalMatches,
    setTabMeta,
  ]);

  // Determine empty state message based on filter state and total results
  // Prioritize showing filter message when filters are active
  const emptyTitle = hasActiveFilters ? "No Results" : "No Matched Cards Yet";
  const emptyDescription = hasActiveFilters
    ? "No cards match your current filters. Try adjusting your search or filters."
    : "Add cards to your wants list and we'll notify you when players in this city have them for trade.";

  // Smart CTA text/link based on store status
  const smartCtaText = hasActiveFilters
    ? undefined
    : isStoreStatusLoading || hasStoresInArea
      ? "Add cards to your trade lists"
      : noStoresFound
        ? undefined
        : "Browse stores nearby";
  const smartCtaLink = hasActiveFilters
    ? undefined
    : isStoreStatusLoading || hasStoresInArea
      ? "/x/lists"
      : noStoresFound
        ? undefined
        : browseStoresUrl;

  // Embedded mode: no card wrapper, no TradeSectionTitle
  if (embedded) {
    return (
      <div className="flex h-full flex-col">
        <div
          className={cn(
            "flex-1 overflow-hidden",
            !hasData && "flex items-center justify-center"
          )}
        >
          {hasData ? (
            <CityMatchedItemsView
              listKey={listKey}
              data={dataResultsMatched}
              isPending={isPendingMatched || isAuthLoading}
              hasNextPage={hasNextPageMatched}
              isFetchingNextPage={isFetchingNextPageMatched}
              fetchNextPage={fetchNextPageMatched}
              activeView={unifiedActiveView}
              maxItems={maxItems}
            />
          ) : (
            <EmptyStateCard
              variant="compact"
              icon={<MatchedCardsIcon className="text-primary h-8 w-8" />}
              title={emptyTitle}
              description={emptyDescription}
              ctaText={
                hasActiveFilters ? undefined : "Add cards to your trade lists"
              }
              ctaLink={hasActiveFilters ? undefined : "/x/lists"}
            />
          )}
        </div>
        {maxItems && hasAdditionalMatches && viewAllLink && (
          <div className="flex justify-center pb-1 pt-3">
            <Link
              href={viewAllLink}
              className="btn btn-soft btn-sm btn-primary gap-1"
            >
              View all
              <ArrowRight size={ICON_SIZE} />
            </Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        hideCard
          ? "md:rounded-box md:bg-base-200 md:p-0 md:shadow-lg"
          : "rounded-box bg-base-200 p-2 shadow-lg"
      )}
    >
      <TradeSectionTitle
        countInMainbar={countInMainbar}
        currentCount={displayedCount}
        total={total}
        type="match"
        viewAllLink={maxItems ? undefined : viewAllLink}
        leftContent={mobileFilterBar}
        isLoading={isPendingMatched || isAuthLoading}
      />
      <div
        className={cn(
          "flex-1 overflow-hidden",
          !hasData && "flex items-center justify-center"
        )}
      >
        {hasData ? (
          <CityMatchedItemsView
            listKey={listKey}
            data={dataResultsMatched}
            isPending={isPendingMatched || isAuthLoading}
            hasNextPage={hasNextPageMatched}
            isFetchingNextPage={isFetchingNextPageMatched}
            fetchNextPage={fetchNextPageMatched}
            activeView={unifiedActiveView}
            maxItems={maxItems}
          />
        ) : (
          <EmptyStateCard
            variant="compact"
            icon={<MatchedCardsIcon className="text-primary h-8 w-8" />}
            title={emptyTitle}
            description={emptyDescription}
            ctaText={smartCtaText}
            ctaLink={smartCtaLink}
          />
        )}
      </div>
      {maxItems && hasAdditionalMatches && viewAllLink && (
        <div className="flex justify-center pb-1 pt-3">
          <Link
            href={viewAllLink}
            className="btn btn-soft btn-sm btn-primary gap-1"
          >
            View all
            <ArrowRight size={ICON_SIZE} />
          </Link>
        </div>
      )}
    </div>
  );
}
